import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Tag, Clock } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/blog/${slug}`).then((r) => setArticle(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = article.meta_title || article.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", article.meta_description || article.excerpt);
    }
  }, [article]);

  if (loading) return (
    <div className="min-h-screen pt-24 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF5200] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!article) return (
    <div className="min-h-screen pt-24 text-center py-20">
      <h1 className="text-2xl font-heading font-bold text-[#09090B] mb-4">Articolo non trovato</h1>
      <Link to="/blog" className="text-[#FF5200] font-bold text-sm uppercase tracking-widest hover:underline">Torna al blog</Link>
    </div>
  );

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="blog-article-page">
      <article className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Back */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-zinc-500 hover:text-[#FF5200] transition-colors mb-8 font-heading" data-testid="blog-back-link">
          <ArrowLeft size={14} /> Torna al blog
        </Link>

        {/* Meta */}
        <div className="flex items-center gap-4 mb-6">
          <span className="text-[10px] font-bold tracking-widest uppercase bg-[#FF5200]/10 text-[#FF5200] px-2 py-1 font-heading">
            {article.category}
          </span>
          <span className="flex items-center gap-1 text-xs text-zinc-400 font-body">
            <Calendar size={12} />
            {new Date(article.published_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
          </span>
          <span className="flex items-center gap-1 text-xs text-zinc-400 font-body">
            <Clock size={12} />
            {Math.max(1, Math.ceil(article.content.replace(/<[^>]*>/g, "").split(" ").length / 200))} min lettura
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black uppercase leading-[0.95] tracking-tight text-[#09090B] mb-8" data-testid="blog-article-title">
          {article.title}
        </h1>

        {/* Excerpt */}
        <p className="text-lg text-zinc-600 font-body leading-relaxed border-l-4 border-[#FF5200] pl-6 mb-10">
          {article.excerpt}
        </p>

        {/* Image */}
        {article.image_url && (
          <div className="aspect-video mb-10 bg-zinc-100">
            <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-zinc max-w-none font-body
            prose-headings:font-heading prose-headings:uppercase prose-headings:tracking-wide
            prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-[#09090B]
            prose-h3:text-lg prose-h3:font-bold prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-[#09090B]
            prose-p:text-base prose-p:leading-relaxed prose-p:text-zinc-600 prose-p:mb-4
            prose-strong:text-[#09090B] prose-a:text-[#FF5200] prose-a:no-underline hover:prose-a:underline
            prose-ul:my-4 prose-li:text-zinc-600
          "
          data-testid="blog-article-content"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-zinc-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={14} className="text-zinc-400" />
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs font-body text-zinc-500 bg-zinc-100 px-3 py-1">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 bg-[#09090B] p-8 md:p-12 text-center">
          <h3 className="text-xl font-heading font-bold uppercase text-white mb-3">Hai bisogno di un preventivo?</h3>
          <p className="text-sm text-zinc-400 font-body mb-6">Calcola il costo della tua spedizione in pochi secondi</p>
          <Link to="/preventivo" className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-6 py-3 text-sm font-bold uppercase tracking-widest font-heading hover:bg-[#e64a00] transition-colors">
            Calcola Preventivo
          </Link>
        </div>
      </article>
    </div>
  );
}
